import React, { useMemo } from "react";
import styles from "../styles/calendario.module.css"; // ajusta si tu ruta real es distinta
import { IoPeopleOutline, IoRestaurantOutline, IoClose, IoWarningOutline } from "react-icons/io5";
import { formatFechaLarga } from "../domain/calendarioDates";
import { isPdf, isExcel, isImage } from "../../eventos/domain/eventoDetalleUtils";
import { buildAlertsForEvento, groupAlertsByPerson } from "../../eventos/domain/notificacionesEvento";
import { formatDate } from "../../eventos/domain/eventoDetalleUtils";

const ALERTAS_HORIZON_DIAS = 180;

/**
 * Modal de detalle para EVENTO / CACERIA
 * - No hace fetch
 * - No navega por sí solo: delega a callbacks
 */
export default function CalendarioDetalleModal({
    show,
    selectedItem,
    modalTheme,
    activeTab,
    setActiveTab,
    participacionesOrdenadas,
    openSubtab,
    setOpenSubtab,
    tabsRef,
    onClose,
    onGoToEvento,
    onGoToPersona,
    onGoToCaceria,
}) {
    const alertGroups = useMemo(() => {
        if (!selectedItem?.data) return [];
        const eventoLike =
            selectedItem.tipo === "EVENTO" ? selectedItem.data : { cacerias: [selectedItem.data] };
        const alerts = buildAlertsForEvento(eventoLike, ALERTAS_HORIZON_DIAS, []) || [];
        return groupAlertsByPerson(alerts);
    }, [selectedItem]);

    const totalAlertas = useMemo(
        () => alertGroups.reduce((acc, g) => acc + g.items.length, 0),
        [alertGroups]
    );
    const hayAlertaVencida = useMemo(
        () => alertGroups.some((g) => g.items.some((a) => a.status === "expired")),
        [alertGroups]
    );

    if (!show || !selectedItem) return null;

    const tabs =
        selectedItem.tipo === "EVENTO"
            ? ["Detalles", "Participantes", "Transfer", "Menús", "Actividades", "Cacerías", "Alertas"]
            : ["Detalles", "Cazadores", "Cacerías", "Alertas"];

    const parts = selectedItem.data?.participaciones || [];

    return (
        <div className={styles["modal-backdrop"]} onClick={onClose}>
            <div
                className={styles["modal-content"]}
                onClick={(e) => e.stopPropagation()}
                style={{
                    "--modalHeader": modalTheme?.header,
                    "--tabActive": modalTheme?.header,
                    "--ring": `0 0 0 3px ${modalTheme?.header}55`,
                }}
            >
                {/* Header */}
                <div className={styles["modal-header"]}>
                    <h2 className={styles["modal-title"]}>
                        {selectedItem.tipo === "EVENTO"
                            ? `Evento: ${selectedItem.data.nombre}`
                            : `Cacería: ${selectedItem.data.nombre || selectedItem.data.fecha}`}
                    </h2>

                    <div className={styles["header-actions"]}>
                        {selectedItem.tipo === "EVENTO" && (
                            <button className={styles["go-btn"]} onClick={onGoToEvento}>
                                Ir al evento
                            </button>
                        )}

                        {selectedItem.tipo === "CACERIA" && (
                            <button className={styles["go-btn"]} onClick={() => onGoToCaceria?.(selectedItem.data.id)}>
                                Editar cacería
                            </button>
                        )}

                        <button className={styles["close-btn"]} onClick={onClose}>
                            <IoClose size={22} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className={styles["modal-tabs"]} ref={tabsRef}>
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            data-tab={tab}
                            className={`${styles["tab-btn"]} ${activeTab === tab ? styles.active : ""}`}
                            onClick={() => setActiveTab(tab)}
                            type="button"
                        >
                            {tab}
                            {tab === "Alertas" && totalAlertas > 0 && (
                                <span
                                    className={styles["tab-alert-badge"]}
                                    style={{ background: hayAlertaVencida ? "#c0392b" : "#e0a800" }}
                                >
                                    {totalAlertas}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className={styles["modal-body"]}>
                    {/* DETALLES */}
                    {activeTab === "Detalles" && (
                        <div className={styles["tab-panel"]}>
                            <div className={styles["caceria-block"]}>
                                {selectedItem.tipo === "EVENTO" ? (
                                    <>
                                        <div className={styles.fieldBlock} style={{ display: "flex" }}>
                                            <strong className={styles.fieldLabel}>Observaciones:</strong>
                                            <div className={styles.fieldValue}>
                                                {selectedItem.data.descripcion || "—"}
                                            </div>
                                        </div>

                                        <p>
                                            <strong>Fecha inicio:</strong> {selectedItem.data.fechaInicio}
                                        </p>
                                        <p>
                                            <strong>Fecha fin:</strong> {selectedItem.data.fechaFin}
                                        </p>
                                        <p>
                                            <strong>Provisional:</strong> {selectedItem.data.esProvisional ? "Sí" : "No"}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p>
                                            <strong>Tipo de Cacería:</strong> {selectedItem.data.tipoCaceriaDescripcion}
                                        </p>
                                        <p>
                                            <strong>Fecha:</strong> {selectedItem.data.fecha}
                                        </p>
                                        <p>
                                            <strong>Documento:</strong> {selectedItem.data.rutaDocumento || "—"}
                                        </p>
                                        <p>
                                            <strong>Provisional:</strong> {selectedItem.data.esProvisional ? "Sí" : "No"}
                                        </p>
                                    </>
                                )}

                                {/* Resumen participantes/cazadores */}
                                {!!parts?.length && (
                                    selectedItem.tipo === "EVENTO" ? (
                                        (() => {
                                            const participantes = participacionesOrdenadas || [];
                                            const grupos = {
                                                cliente: participantes.filter((p) => p?.persona?.tipoPersona === "CLIENTE"),
                                                staff: participantes.filter((p) => p?.persona?.tipoPersona === "STAFF"),
                                                otro: participantes.filter(
                                                    (p) => p?.persona?.tipoPersona !== "CLIENTE" && p?.persona?.tipoPersona !== "STAFF"
                                                ),
                                            };

                                            return (
                                                <div className={styles["participantes-summary"]}>
                                                    <h4>
                                                        Total participantes:{" "}
                                                        <span className={styles["total-count"]}>{participantes.length}</span>
                                                    </h4>
                                                    <div className={styles["grupo-cards"]}>
                                                        <div className={`${styles["grupo-card"]} ${styles["cliente"]}`}>
                                                            <strong>Clientes: </strong><span>{grupos.cliente.length}</span>
                                                        </div>
                                                        <div className={`${styles["grupo-card"]} ${styles["staff"]}`}>
                                                            <strong>Staff: </strong><span>{grupos.staff.length}</span>
                                                        </div>
                                                        <div className={`${styles["grupo-card"]} ${styles["otro"]}`}>
                                                            <strong>Otros: </strong><span>{grupos.otro.length}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        (() => {
                                            const cazadores = (participacionesOrdenadas || []).filter((p) => p?.rol?.rol === "CAZADOR");
                                            return (
                                                <div className={styles["participantes-summary"]}>
                                                    <h4>
                                                        Total cazadores:{" "}
                                                        <span className={styles["total-count"]}>{cazadores.length}</span>
                                                    </h4>
                                                </div>
                                            );
                                        })()
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* PARTICIPANTES / CAZADORES */}
                    {(activeTab === "Participantes" || activeTab === "Cazadores") && (
                        <>
                            {parts?.length > 0 ? (
                                <div className={styles["participants-grid"]}>
                                    {(participacionesOrdenadas || [])
                                        .filter((p) => (activeTab === "Cazadores" ? p?.rol?.rol === "CAZADOR" : true))
                                        .map((p) => (
                                            <div key={p.id} className={styles["participant-card"]}>
                                                {p.persona?.foto ? (
                                                    <img
                                                        src={p.persona.foto}
                                                        alt={`${p.persona.nombre} ${p.persona.apellidos}`}
                                                        className={styles["avatar-circle"]}
                                                    />
                                                ) : (
                                                    <div className={styles["avatar-circle"]}>
                                                        <span>
                                                            {p.persona?.nombre?.charAt(0) || "?"}
                                                            {p.persona?.apellidos?.charAt(0) || "?"}
                                                        </span>
                                                    </div>
                                                )}

                                                <h4>
                                                    {p.persona?.nombre} {p.persona?.apellidos}
                                                </h4>
                                                <p>{p.persona?.nickname || "\u00A0"}</p>

                                                {p.persona?.id && (
                                                    <button
                                                        type="button"
                                                        className={styles["link-chip"]}
                                                        onClick={() => onGoToPersona?.(p.persona.id)}
                                                    >
                                                        Editar perfil
                                                    </button>
                                                )}

                                                {/* Subtabs */}
                                                <div className={styles.subtabs}>
                                                    {["Datos", "Armas", "Lic.Armas", "Notas"].map((st) => (
                                                        <button
                                                            key={st}
                                                            type="button"
                                                            className={`${styles["subtab-btn"]} ${openSubtab?.[p.id] === st ? styles.active : ""
                                                                }`}
                                                            onClick={() =>
                                                                setOpenSubtab((prev) => ({
                                                                    ...prev,
                                                                    [p.id]: prev?.[p.id] === st ? null : st,
                                                                }))
                                                            }
                                                        >
                                                            {st}
                                                        </button>
                                                    ))}
                                                </div>

                                                {/* Contenido subtabs */}
                                                {openSubtab?.[p.id] === "Datos" && (
                                                    <div className={styles["subtab-content"]}>
                                                        <p><strong>Teléfono:</strong> {p.persona?.telefono}</p>
                                                        <p><strong>Dirección:</strong> {p.persona?.direccion}</p>
                                                        <p><strong>Documento:</strong> {p.persona?.numeroDocumento}</p>
                                                        <p><strong>Caducidad:</strong> {p.persona?.fechaCaducidad}</p>
                                                    </div>
                                                )}

                                                {openSubtab?.[p.id] === "Armas" && (
                                                    <div className={styles["subtab-content"]}>
                                                        {p.persona?.armas?.length > 0 ? (
                                                            p.persona.armas.map((arma) => (
                                                                <div key={arma.id} className={styles["mini-card"]}>
                                                                    <p><strong>{arma.marca} {arma.modelo}</strong></p>
                                                                    <p>Calibre: {arma.calibreDescripcion}</p>
                                                                    <p>Tipo: {arma.tipoArmaDescripcion}</p>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p>No tiene armas registradas.</p>
                                                        )}
                                                    </div>
                                                )}

                                                {openSubtab?.[p.id] === "Lic.Armas" && (
                                                    <div className={styles["subtab-content"]}>
                                                        {p.persona?.licenciasArmas?.map((lic) => (
                                                            <div key={lic.id} className={styles["mini-card"]}>
                                                                <p><strong>{lic.tipoDescripcion}</strong></p>
                                                                <p>Expedición: {lic.fechaDeExpedicion}</p>
                                                                <p>Validez: {lic.fechaValidez}</p>
                                                            </div>
                                                        ))}
                                                        {p.persona?.licenciasCaza?.map((lc) => (
                                                            <div key={lc.id} className={styles["mini-card"]}>
                                                                <p><strong>Caza #{lc.numero}</strong></p>
                                                                <p>Expedición: {lc.fechaDeExpedicion}</p>
                                                                <p>Caducidad: {lc.fechaCaducidad}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {openSubtab?.[p.id] === "Notas" && (
                                                    <div className={styles["subtab-content"]}>
                                                        <p>{p.persona?.comentarios}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className={styles["caceria-block"]}>
                                    <p>No hay participantes registrados.</p>
                                </div>
                            )}
                        </>
                    )}



                    {/* TRANSFER (solo EVENTO) */}
                    {selectedItem.tipo === "EVENTO" && activeTab === "Transfer" && (
                        <div className={styles["tab-panel"]} style={{ height: "100%", minHeight: 0 }}>
                            <div className={styles["caceria-block"]} style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
                                <h4 style={{ marginTop: 0 }}>Transfer</h4>

                                <div
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflowY: "auto",
                                        whiteSpace: "pre-wrap",
                                        lineHeight: 1.55,
                                    }}
                                >
                                    {(selectedItem.data?.transfer ?? "").trim() || "—"}
                                </div>
                            </div>
                        </div>
                    )}


                    {/* MENÚS (solo EVENTO) */}
                    {selectedItem.tipo === "EVENTO" && activeTab === "Menús" && (
                        <div className={styles["tab-panel"]}>
                            {selectedItem.data.menus?.length > 0 ? (
                                selectedItem.data.menus.map((menu) => (
                                    <div key={menu.id} className={styles["caceria-block"]}>
                                        <h4>
                                            <IoRestaurantOutline /> {menu.nombre} - {formatFechaLarga(menu.fecha)} - {menu.lugar} - {menu.comensales} comensales
                                        </h4>
                                        <ul>
                                            {menu.platos?.map((plato) => (
                                                <li key={plato.id}>{plato.nombre}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            ) : (
                                <div className={styles["caceria-block"]}>
                                    <p>No hay menús asignados.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ACTIVIDADES (solo EVENTO) */}
                    {selectedItem.tipo === "EVENTO" && activeTab === "Actividades" && (
                        <div className={styles["tab-panel"]}>
                            <div className={styles["caceria-block"]}>
                                {selectedItem.data.actividades?.length > 0 ? (
                                    <ul>
                                        {selectedItem.data.actividades.map((act) => {
                                            const participantesEnActividad = (selectedItem.data.participaciones || []).filter((p) =>
                                                p.actividades?.some((a) => a.id === act.id)
                                            );

                                            return (
                                                <li key={act.id}>
                                                    <strong>{act.nombre}</strong> ({act.tipo}) - {formatFechaLarga(act.fecha)}
                                                    <br />
                                                    <small>Total participantes: {participantesEnActividad.length}</small>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p>No hay actividades programadas.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CACERÍAS */}
                    {activeTab === "Cacerías" && (
                        <div className={styles["tab-panel"]}>
                            {selectedItem.tipo === "EVENTO" && selectedItem.data.cacerias?.length > 0 ? (
                                selectedItem.data.cacerias.map((cac) => (
                                    <div key={cac.id} className={styles["caceria-block"]}>
                                        <h4>
                                            <IoPeopleOutline /> {cac.nombre ? `${cac.nombre} — ` : ""}
                                            {cac.tipoCaceriaDescripcion} - {cac.fecha}
                                        </h4>
                                        <button
                                            type="button"
                                            className={styles["link-chip"]}
                                            onClick={() => onGoToCaceria?.(cac.id)}
                                        >
                                            Editar cacería
                                        </button>
                                        {cac.rutaDocumento ? (
                                            <a
                                                href={cac.rutaDocumento}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles["link-chip"]}
                                                title={
                                                    isPdf(cac.rutaDocumento)
                                                        ? "Ver PDF"
                                                        : isExcel(cac.rutaDocumento)
                                                            ? "Abrir Excel/CSV"
                                                            : isImage(cac.rutaDocumento)
                                                                ? "Ver imagen"
                                                                : "Abrir documento"
                                                }
                                            >
                                                Ver documento
                                            </a>
                                        ) : (
                                            <span className={styles.muted}>Sin documento</span>
                                        )}

                                        {cac.enlaces?.map((url, i) => (
                                            <a
                                                key={i}
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles["link-chip"]}
                                                title={url}
                                            >
                                                Enlace {cac.enlaces.length > 1 ? i + 1 : ""}
                                            </a>
                                        ))}

                                        {cac.participaciones?.length > 0 && (
                                            <>
                                                <h5><IoPeopleOutline /> Cazadores</h5>
                                                <ul>
                                                    {cac.participaciones
                                                        .filter((cp) => cp.rol?.rol === "CAZADOR")
                                                        .map((cp) => (
                                                            <li key={cp.id}>
                                                                <strong>{cp.persona.nombre} {cp.persona.apellidos}</strong>
                                                            </li>
                                                        ))}
                                                </ul>
                                            </>
                                        )}
                                    </div>
                                ))
                            ) : selectedItem.tipo === "CACERIA" ? (
                                <div className={styles["caceria-block"]}>
                                    <h4>
                                        <IoPeopleOutline /> {selectedItem.data.nombre ? `${selectedItem.data.nombre} — ` : ""}
                                        {selectedItem.data.tipoCaceriaDescripcion} - {selectedItem.data.fecha}
                                    </h4>
                                    <p><strong>Documento:</strong> {selectedItem.data.rutaDocumento}</p>

                                    {selectedItem.data.enlaces?.map((url, i) => (
                                        <a
                                            key={i}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={styles["link-chip"]}
                                            title={url}
                                        >
                                            Enlace {selectedItem.data.enlaces.length > 1 ? i + 1 : ""}
                                        </a>
                                    ))}

                                    {selectedItem.data.participaciones?.length > 0 && (
                                        <>
                                            <h5><IoPeopleOutline /> Cazadores</h5>
                                            <ul>
                                                {selectedItem.data.participaciones
                                                    .filter((cp) => cp.rol?.rol === "CAZADOR")
                                                    .map((cp) => (
                                                        <li key={cp.id}>
                                                            <strong>{cp.persona.nombre} {cp.persona.apellidos}</strong>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className={styles["caceria-block"]}>
                                    <p>No hay cacerías relacionadas.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ALERTAS */}
                    {activeTab === "Alertas" && (
                        <div className={styles["tab-panel"]}>
                            {alertGroups.length === 0 ? (
                                <div className={styles["alert-empty"]}>
                                    <IoWarningOutline size={28} />
                                    <p>
                                        Sin alertas de documentación para este{" "}
                                        {selectedItem.tipo === "EVENTO" ? "evento" : "cacería"}.
                                    </p>
                                </div>
                            ) : (
                                alertGroups.map((grp) => (
                                    <div key={grp.personId} className={styles["alert-card"]}>
                                        <div className={styles["alert-card-head"]}>
                                            <IoWarningOutline size={18} />
                                            <span>{grp.personName}</span>
                                        </div>

                                        <div className={styles["alert-list"]}>
                                            {grp.items.map((a) => (
                                                <div key={a.id} className={styles["alert-row"]}>
                                                    <div className={styles["alert-row-info"]}>
                                                        <span className={styles["alert-row-label"]}>{a.label}</span>
                                                        <span className={styles["alert-row-meta"]}>
                                                            {[a.meta, a.fecha ? formatDate(a.fecha) : null]
                                                                .filter(Boolean)
                                                                .join(" • ") || " "}
                                                        </span>
                                                    </div>

                                                    <span
                                                        className={`${styles["alert-status"]} ${styles[`alert-status-${a.status}`]}`}
                                                    >
                                                        {a.status === "expired"
                                                            ? "Vencida"
                                                            : a.status === "missing"
                                                                ? "Sin fecha"
                                                                : `Vence en ${a.daysLeft} días`}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
